/**
 * Step 3: Profile Review & Complete
 * Review auto-populated or manually entered data
 * Add additional details (mission, website, phone)
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle } from 'lucide-react';
import apiClient from '@/lib/apiClient';

export default function ProfileSetupStep({ organizationData, onComplete, onBack }) {
  const [formData, setFormData] = useState({
    ...organizationData.organization,
    mission: '',
    website: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/onboarding/profile', formData);
      onComplete(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          Complete Your Profile
        </h2>
        <p className="text-slate-400">
          Review your organization details and add additional information
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert className="bg-red-500/10 border-red-500/50">
            <AlertDescription className="text-red-400">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Auto-populated data (read-only or editable) */}
        <div className="bg-slate-700/30 rounded-lg p-6 border border-slate-600">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <h3 className="text-white font-semibold">Organization Information</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="legal_name" className="text-white">
                Legal Name *
              </Label>
              <Input
                id="legal_name"
                name="legal_name"
                type="text"
                required
                value={formData.legal_name}
                onChange={handleChange}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {formData.bn_number && (
              <div className="space-y-2">
                <Label className="text-white">BN Number</Label>
                <div className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-slate-300">
                  {formData.bn_number}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="city" className="text-white">
                City *
              </Label>
              <Input
                id="city"
                name="city"
                type="text"
                required
                value={formData.city}
                onChange={handleChange}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="province" className="text-white">
                Province *
              </Label>
              <div className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-slate-300">
                {formData.province}
              </div>
            </div>

            {formData.revenue && (
              <div className="space-y-2">
                <Label className="text-white">Revenue (Latest)</Label>
                <div className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-slate-300">
                  ${formData.revenue.toLocaleString()}
                </div>
              </div>
            )}

            {formData.designation && (
              <div className="space-y-2">
                <Label className="text-white">Designation</Label>
                <div className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-slate-300">
                  {formData.designation}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional information */}
        <div className="space-y-4">
          <h3 className="text-white font-semibold">Additional Information</h3>

          <div className="space-y-2">
            <Label htmlFor="mission" className="text-white">
              Mission Statement
            </Label>
            <Textarea
              id="mission"
              name="mission"
              value={formData.mission}
              onChange={handleChange}
              className="bg-slate-700 border-slate-600 text-white min-h-[100px]"
              placeholder="Describe your organization's mission and objectives..."
            />
            <p className="text-sm text-slate-400">
              This helps us better match you with relevant grant opportunities
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="website" className="text-white">
                Website
              </Label>
              <Input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleChange}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="https://yourorg.ca"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white">
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Next Steps Info */}
        <Alert className="bg-blue-500/10 border-blue-500/50">
          <AlertDescription className="text-blue-300">
            <strong>Next Step:</strong> After completing your profile, you'll be able to create funding projects to discover grant opportunities tailored to your specific programs and initiatives.
          </AlertDescription>
        </Alert>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 border-slate-600 text-slate-300"
            disabled={loading}
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
          >
            {loading ? 'Completing...' : 'Complete Profile'}
          </Button>
        </div>
      </form>
    </div>
  );
}
